import { Button, InputError, Select, Spinner } from '@foodsby/nutrient'
import cn from 'classnames'
import { Formik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'

import { useMyReferralWithEnabledDetails } from '../../hooks/referral'
import {
  addFriendStart,
  clearAutocompleteSuggestions,
  getAutocompleteFriendsStart,
} from '../../redux/modules/friends'
import { validAutocomplete } from '../../util/validators'
import { SpinnerCard } from '../common/Layout'
import FriendLabel from '../community/FriendLabel'
import { CopyReferral } from '../community/Referral'

const Null = () => null
const Loading = () => <Spinner className="spinner-xs primary" />

const CustomOption = ({ data, innerProps, innerRef, isFocused, selectProps }) => {
  return (
    <div
      className={cn({
        'autocomplete-option': true,
        'autocomplete-option__focused': isFocused,
      })}
      {...innerProps}
      ref={innerRef}
    >
      <div className="row middle-xs">
        <div className="col-xs-10">
          <div className="avatar-container">
            <FriendLabel
              avatarId={data.avatarId}
              firstName={data.firstName}
              isWebpSupported={selectProps.isWebpSupported}
              key={data.userId}
              lastName={data.lastName}
              showEmail={false}
              showName={false}
              size="small"
            />
          </div>
          <div className="friend-label">{data.label}</div>
        </div>
        <div className="col-xs-2 add-icon">
          <img alt="+" src="https://images.foodsby.com/conweb/add-friend-icon.svg" width="20px" />
        </div>
      </div>
    </div>
  )
}

const AutoCompleteComponents = {
  DropdownIndicator: Null,
  LoadingIndicator: Loading,
  Option: CustomOption,
}

export const AutoComplete = ({
  clearAutocompleteSuggestions,
  getAutocompleteFriendsStart,
  invited,
  isLoadingAutocomplete,
  isWebpSupported,
  name,
  setFieldValue,
  suggestions,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [_input, setInput] = useState(null)

  const normalizeValue = v => (v?.value ? v.value : v || '')

  useEffect(() => {
    if (invited) {
      clearAutocompleteSuggestions()
      setInputValue('')
      setInput(null)
      setFieldValue('email', '')
    }
  }, [clearAutocompleteSuggestions, invited, setFieldValue])

  const handleInputAutocomplete = (v, action) => {
    if (v?.trim() !== '' && v?.length === 3) {
      getAutocompleteFriendsStart(v)
    }
    if (
      (suggestions?.length > 0 && v?.length < 3) ||
      (v === '' && action.action === 'input-change')
    ) {
      clearAutocompleteSuggestions()
    }
  }

  function handleSelectOnInputChange(v, action) {
    // Keep partial text after going out of focus
    if (action.action !== 'input-blur' && action.action !== 'menu-close') {
      setInputValue(v)
    }
    v && setFieldValue('email', normalizeValue(v))
    handleInputAutocomplete(v, action)
  }

  return (
    <>
      <Select
        className="invite-friend-input"
        components={AutoCompleteComponents}
        escapeClearsValue
        inputValue={inputValue}
        isClearable
        isLoading={isLoadingAutocomplete}
        isWebpSupported={isWebpSupported}
        name={name}
        noOptionsMessage={
          inputValue?.length > 3 && !inputValue?.includes('@')
            ? () =>
                `We couldn’t find anyone by that name. You can add a friend by entering their email address.`
            : Null
        }
        onBlur={() => {
          if (suggestions?.length > 0 && inputValue === '' && _input === null) {
            clearAutocompleteSuggestions()
          }
        }}
        onChange={v => {
          setInput(v)
          setFieldValue('email', normalizeValue(v))
        }}
        onInputChange={handleSelectOnInputChange}
        options={suggestions}
        placeholder="Name or Email"
        value={_input || null}
      />
    </>
  )
}

export const FriendRequestForm = ({
  clearAutocompleteSuggestions,
  getAutocompleteFriendsStart,
  isAddingFriends,
  isLoadingAutocomplete,
  isWebpSupported,
  onSubmit,
  sendFriendRequestError,
  suggestions,
}) => {
  const [invited, setInvited] = useState(false)
  const [invitedByRequestSidebar, setInvitedByRequestSidebar] = useState(false)
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
    } else if (!isAddingFriends && !sendFriendRequestError && invitedByRequestSidebar) {
      setInvitedByRequestSidebar(false)
      setInvited(true)
      setTimeout(() => setInvited(false), 1500)
    }
  }, [isAddingFriends, sendFriendRequestError]) // eslint-disable-line

  return (
    <Formik
      initialValues={{ email: '' }}
      onSubmit={values => {
        setInvitedByRequestSidebar(true)
        onSubmit(values)
      }}
      render={props => (
        <form className="invite-friend-form" onSubmit={props.handleSubmit}>
          <div className="Select">
            <AutoComplete
              clearAutocompleteSuggestions={clearAutocompleteSuggestions}
              error={props.errors.email}
              getAutocompleteFriendsStart={getAutocompleteFriendsStart}
              handleSubmit={props.handleSubmit}
              invited={invited}
              isLoadingAutocomplete={isLoadingAutocomplete}
              isWebpSupported={isWebpSupported}
              name="email"
              onBlur={props.handleBlur}
              onChange={props.handleChange}
              setFieldValue={props.setFieldValue}
              style={{ width: '100%' }}
              suggestions={suggestions}
              touched={props.touched}
              value={props.values.email}
            />
            <Button
              className={cn({
                'invite-friend-button': !invited,
                'invited-friend-button': invited,
              })}
              disabled={isAddingFriends || props.errors.email || props.values.email === ''}
              type="submit"
            >
              {isAddingFriends && invitedByRequestSidebar ? (
                <Spinner className="spinner-xs primary" />
              ) : invited ? (
                <>
                  <img alt="" src="https://images.foodsby.com/www/theme/check.svg" /> INVITED
                </>
              ) : (
                'ADD FRIEND'
              )}
            </Button>
          </div>
        </form>
      )}
      validate={values => {
        let errors = {}
        const msg = validAutocomplete(values.email)
        if (msg) {
          errors.email = msg
        }
        return errors
      }}
    />
  )
}

const FriendRequest = ({
  addFriendStart,
  clearAutocompleteSuggestions,
  getAutocompleteFriendsStart,
  isAddingFriends,
  isLoadingAutocomplete,
  isWebpSupported,
  sendFriendRequestError,
  success,
  suggestions,
}) => {
  const { referralAndEnabledDetails } = useMyReferralWithEnabledDetails()
  const referralSupported = referralAndEnabledDetails?.referralEnabledDetails !== undefined
  const referralReward =
    referralAndEnabledDetails?.referralEnabledDetails?.rewardAmountPennies / 100

  return (
    <SpinnerCard className="invite-friends-card">
      <div className="row center-xs">
        <div className="col-xs-12">
          <img alt="Friends" src="https://images.foodsby.com/conweb/coworkers1x.png" />
        </div>
      </div>
      <h4 className="center-xs invite-friends-card-heading">Find Friends</h4>
      <p className="center-xs connect-coworker-p">
        Connect with your work friends to share favorites and daily orders.
      </p>
      <FriendRequestForm
        clearAutocompleteSuggestions={clearAutocompleteSuggestions}
        getAutocompleteFriendsStart={getAutocompleteFriendsStart}
        isAddingFriends={isAddingFriends}
        isLoadingAutocomplete={isLoadingAutocomplete}
        isWebpSupported={isWebpSupported}
        onSubmit={addFriendStart}
        sendFriendRequestError={sendFriendRequestError}
        success={success}
        suggestions={suggestions}
      />
      {sendFriendRequestError && (
        <InputError
          className="friend-request-error row center-xs"
          isOpened={sendFriendRequestError && sendFriendRequestError !== null}
        >
          {sendFriendRequestError}
        </InputError>
      )}
      {referralSupported && (
        <>
          <div className="hr-text">OR</div>
          <CopyReferral referralAndEnabledDetails={referralAndEnabledDetails} />
          <p className="invite-friends-card-subhead">
            {`Invite your work friends to Foodsby and you’ll both get a $${referralReward} credit when
            they place their first order and it's delivered.`}
          </p>
        </>
      )}
    </SpinnerCard>
  )
}

const mapStateToProps = state => {
  const {
    clearAutocompleteSuggestions,
    isAddingFriends,
    isLoadingAutocomplete,
    sendFriendRequestError,
    success,
    suggestions,
  } = state.friends
  const { isWebpSupported } = state.browser
  return {
    clearAutocompleteSuggestions,
    isAddingFriends,
    isLoadingAutocomplete,
    isWebpSupported,
    sendFriendRequestError,
    success,
    suggestions,
  }
}

const mapDispatchToProps = {
  addFriendStart,
  clearAutocompleteSuggestions,
  getAutocompleteFriendsStart,
}

export default connect(mapStateToProps, mapDispatchToProps)(FriendRequest)
